// ═══════════════════════════════════════════════════════════════
// RollCall — Personnel Roster
// ═══════════════════════════════════════════════════════════════

import { useEffect, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Title, Text, Paper, Group, Badge, Button, TextInput, Select, ActionIcon, Modal,
} from '../../components/gab/GabComponents';
import {
  IconSearch, IconPlus, IconPencil, IconTrash, IconEye, IconEyeOff, IconFileImport, IconId,
} from '@tabler/icons-react';
import { notifications } from '../../lib/notifications';
import { useRollCallStore } from '../../store/rollcallStore';
import { useModulePermission } from '../../hooks/useModulePermission';

const SORT_OPTIONS = [
  { value: 'name_asc', label: 'Name (A-Z)' },
  { value: 'name_desc', label: 'Name (Z-A)' },
  { value: 'agency_asc', label: 'Agency (A-Z)' },
  { value: 'newest', label: 'Newest First' },
];

const STATUS_OPTIONS = [
  { value: 'all', label: 'All' },
  { value: 'active', label: 'Active' },
  { value: 'inactive', label: 'Inactive' },
];

export default function PersonnelList() {
  const navigate = useNavigate();
  const { canEdit } = useModulePermission('rollcall');
  const { personnel, loadPersonnel, deletePersonnel } = useRollCallStore();
  const PAGE_SIZE = 25;
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('active');
  const [agencyFilter, setAgencyFilter] = useState<string | null>(null);
  const [sortBy, setSortBy] = useState('name_asc');
  const [revealedPins, setRevealedPins] = useState<Record<number, boolean>>({});
  const [deleteTarget, setDeleteTarget] = useState<typeof personnel[0] | null>(null);

  useEffect(() => { loadPersonnel(); }, []);

  const agencies = useMemo(() => {
    const set = new Set(personnel.map(p => p.agency).filter(Boolean));
    return [...set].sort().map(a => ({ value: a, label: a }));
  }, [personnel]);

  const filtered = useMemo(() => {
    let list = personnel;
    if (statusFilter !== 'all') list = list.filter(p => p.status === statusFilter);
    if (agencyFilter) list = list.filter(p => p.agency === agencyFilter);
    if (search) {
      const q = search.toLowerCase();
      list = list.filter(p =>
        `${p.firstName || ''} ${p.lastName || ''}`.toLowerCase().includes(q) ||
        (p.agency || '').toLowerCase().includes(q) ||
        (p.position || '').toLowerCase().includes(q) ||
        (p.pin || '').includes(q)
      );
    }
    return [...list].sort((a, b) => {
      switch (sortBy) {
        case 'name_asc': return `${a.lastName} ${a.firstName}`.localeCompare(`${b.lastName} ${b.firstName}`);
        case 'name_desc': return `${b.lastName} ${b.firstName}`.localeCompare(`${a.lastName} ${a.firstName}`);
        case 'agency_asc': return (a.agency || '').localeCompare(b.agency || '');
        case 'newest': return (b.datecreated || '').localeCompare(a.datecreated || '');
        default: return 0;
      }
    });
  }, [personnel, statusFilter, agencyFilter, search, sortBy]);

  useEffect(() => { setPage(1); }, [search, statusFilter, agencyFilter, sortBy]);
  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const pageRows = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      await deletePersonnel(deleteTarget.id);
      const displayName = (deleteTarget.firstName || deleteTarget.lastName) ? `${deleteTarget.firstName} ${deleteTarget.lastName}`.trim() : deleteTarget.fullName;
      notifications.show({ message: `${displayName} removed`, color: 'gray' });
      setDeleteTarget(null);
    } catch (err: any) {
      notifications.show({ message: err?.message || 'Failed to delete', color: 'red' });
    }
  };

  return (
    <div>
      <Group justify="space-between" mb="md">
        <Title order={3} c="black">Personnel Roster</Title>
        <Group gap="xs">
          <Button size="xs" variant="light" leftSection={<IconId size={14} />}
            onClick={() => navigate('/rollcall/personnel/badges')}>QR Badges</Button>
          {canEdit && <Button size="xs" variant="light" leftSection={<IconFileImport size={14} />}
            onClick={() => navigate('/rollcall/personnel/import')}>Import</Button>}
          {canEdit && <Button size="xs" leftSection={<IconPlus size={14} />}
            onClick={() => navigate('/rollcall/personnel/new')}>Add Personnel</Button>}
        </Group>
      </Group>

      <Group mb="md" gap="sm">
        <TextInput placeholder="Search name, agency, PIN..." leftSection={<IconSearch size={14} />}
          value={search} onChange={e => setSearch(e.target.value)} size="xs" style={{ flex: 1, minWidth: 200 }} />
        <Select data={STATUS_OPTIONS} value={statusFilter}
          onChange={v => setStatusFilter(v ?? 'all')} size="xs" w={120} />
        <Select data={agencies} value={agencyFilter} onChange={setAgencyFilter}
          placeholder="Agency" clearable searchable size="xs" w={180} />
        <Select data={SORT_OPTIONS} value={sortBy} onChange={v => setSortBy(v ?? 'name_asc')}
          size="xs" w={160} />
      </Group>

      <Text size="xs" c="dimmed" mb="sm">Showing {filtered.length} of {personnel.length}</Text>

      {filtered.length === 0 ? (
        <Paper p="xl" radius="sm" withBorder>
          <Text ta="center" c="dimmed">No personnel found.</Text>
        </Paper>
      ) : (
        <Paper p={0} radius="sm" withBorder>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead>
                <tr style={{ borderBottom: '2px solid var(--frost)' }}>
                  <th style={{ textAlign: 'left', padding: '10px 12px', fontWeight: 600 }}>Name</th>
                  <th style={{ textAlign: 'left', padding: '10px 12px', fontWeight: 600 }}>Agency</th>
                  <th style={{ textAlign: 'left', padding: '10px 12px', fontWeight: 600 }}>Position</th>
                  <th style={{ textAlign: 'left', padding: '10px 12px', fontWeight: 600 }}>PIN</th>
                  <th style={{ textAlign: 'left', padding: '10px 12px', fontWeight: 600 }}>Status</th>
                  <th style={{ padding: '10px 12px' }}></th>
                </tr>
              </thead>
              <tbody>
                {pageRows.map(p => (
                  <tr key={p.id} style={{ borderBottom: '1px solid var(--frost)' }}>
                    <td style={{ padding: '8px 12px', fontWeight: 600 }}>
                      {(p.firstName || p.lastName) ? `${p.firstName} ${p.lastName}`.trim() : (p.fullName || '—')}
                    </td>
                    <td style={{ padding: '8px 12px' }}>{p.agency || '—'}</td>
                    <td style={{ padding: '8px 12px', textTransform: 'capitalize' }}>
                      {(p.position || '—').replace(/_/g, ' ')}
                    </td>
                    <td style={{ padding: '8px 12px' }}>
                      <Group gap={4}>
                        <Text size="xs" ff="monospace">{revealedPins[p.id] ? p.pin : '••••'}</Text>
                        <ActionIcon size="xs" variant="subtle"
                          onClick={() => setRevealedPins(prev => ({ ...prev, [p.id]: !prev[p.id] }))}>
                          {revealedPins[p.id] ? <IconEyeOff size={12} /> : <IconEye size={12} />}
                        </ActionIcon>
                      </Group>
                    </td>
                    <td style={{ padding: '8px 12px' }}>
                      <Badge color={p.status === 'active' ? 'green' : 'gray'} size="xs">{p.status}</Badge>
                    </td>
                    <td style={{ padding: '8px 12px' }}>
                      <Group gap={4}>
                        <ActionIcon size="sm" variant="subtle" color="blue"
                          title="Generate badge"
                          onClick={() => navigate(`/rollcall/personnel/badges?id=${p.id}`)}>
                          <IconId size={14} />
                        </ActionIcon>
                        {canEdit && <ActionIcon size="sm" variant="subtle"
                          onClick={() => navigate(`/rollcall/personnel/${p.id}/edit`)}>
                          <IconPencil size={14} />
                        </ActionIcon>}
                        {canEdit && <ActionIcon size="sm" variant="subtle" color="red"
                          onClick={() => setDeleteTarget(p)}>
                          <IconTrash size={14} />
                        </ActionIcon>}
                      </Group>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Paper>
      )}

      {totalPages > 1 && (
        <Group justify="center" mt="sm" gap="xs">
          <Button variant="subtle" size="xs" disabled={page === 1} onClick={() => setPage(1)}>««</Button>
          <Button variant="subtle" size="xs" disabled={page === 1} onClick={() => setPage(p => p - 1)}>‹ Prev</Button>
          {Array.from({ length: Math.min(totalPages, 7) }, (_, i) => {
            const p = totalPages <= 7 ? i + 1 : page <= 4 ? i + 1 : page >= totalPages - 3 ? totalPages - 6 + i : page - 3 + i;
            return <Button key={p} variant={p === page ? 'filled' : 'subtle'} color="blue" size="xs" onClick={() => setPage(p)}>{p}</Button>;
          })}
          <Button variant="subtle" size="xs" disabled={page === totalPages} onClick={() => setPage(p => p + 1)}>Next ›</Button>
          <Button variant="subtle" size="xs" disabled={page === totalPages} onClick={() => setPage(totalPages)}>»»</Button>
        </Group>
      )}

      {/* Delete Confirm */}
      <Modal opened={!!deleteTarget} onClose={() => setDeleteTarget(null)}
        title="Remove Personnel" centered size="sm">
        <Text size="sm" mb="md">
          Remove {deleteTarget?.firstName} {deleteTarget?.lastName} from the roster?
        </Text>
        <Group justify="flex-end">
          <Button variant="light" onClick={() => setDeleteTarget(null)}>Cancel</Button>
          <Button color="red" onClick={handleDelete}>Remove</Button>
        </Group>
      </Modal>
    </div>
  );
}
